<template>
  <FencingBanner
    v-if="isFenced"
    :headline="t(`Reviews.${subject}.view.media.banner.badgesFencingHeadline`)"
    :feature-list="[
      t(`Reviews.${subject}.view.media.banner.badgesFencingItem1`),
      t(`Reviews.${subject}.view.media.banner.badgesFencingItem2`),
      t(`Reviews.${subject}.view.media.banner.badgesFencingItem3`),
      t(`Reviews.${subject}.view.media.banner.badgesFencingItem4`),
    ]"
    :cta-label="t(`Reviews.${subject}.view.media.banner.badgesCtaLabel`)"
    :cta-link="t(`Reviews.${subject}.view.media.banner.badgesCtaLink`)"
    :banner-image="badgesFencingImageUrl"
    placement="banner"
    :entity-type="entityType"
    :entity-id="entityId"
  />
  <div
    v-if="generatedAssignedBadges.length > 0 || generatedExpiredBadges.length > 0"
    class="flex flex-col py-6"
    :class="{ '!text-disabled': isFenced }"
  >
    <div class="pb-8">
      <div class="flex items-center gap-2 pb-2 txt-body-m-bold">
        {{ t(`Reviews.${subject}.view.media.badgesTitle.top`) }}
        <TooltipWrapper v-if="!isFenced">
          <template #trigger>
            <a v-if="helpLink" :href="helpLink" rel="noopener noreferrer" target="_blank"
              ><CircleHelpIcon class="lucide-icon-s text-icon-subtlest"
            /></a>
            <CircleHelpIcon v-else class="lucide-icon-s text-icon-subtlest" />
          </template>
          <template #content>
            <div v-html="helpTooltip"></div>
          </template>
        </TooltipWrapper>
      </div>
      <div class="badge-description-top txt-body-s-regular" v-html="t(`Reviews.${subject}.view.media.badgesDescription.top`)"></div>
    </div>
    <div class="pt-2 pb-2 txt-body-m-bold">{{ t(`Reviews.${subject}.view.media.currentBadgesTitle`) }}</div>
    <ResourcesList
      v-if="generatedAssignedBadges.length > 0"
      data-testid="resources-current-badges"
      class="pt-2"
      :resources="generatedAssignedBadges"
      image-preview-size="s"
      show-download
      :is-fenced="isFenced"
      :entity-type="entityType"
      :entity-id="entityId"
      resource-type="badge"
    />
    <div v-else v-html="t(`Reviews.${subject}.view.media.noAssignedBadges`)" />
    <template v-if="generatedExpiredBadges.length > 0">
      <div class="mt-10 pt-2 pb-2 txt-body-m-bold">{{ t(`Reviews.${subject}.view.media.previousBadgesTitle`) }}</div>
      <ResourcesList
        data-testid="resources-previous-badges"
        class="pt-2"
        :resources="generatedExpiredBadges"
        image-preview-size="s"
        show-download
        :is-fenced="isFenced"
        :entity-type="entityType"
        :entity-id="entityId"
        resource-type="badge"
      />
    </template>
  </div>
  <div v-else>
    <div class="flex flex-col items-center justify-center py-6">
      <div class="mb-4">
        <img src="./no-badges.png" class="w-60" />
      </div>
      <div class="mb-1 txt-headline-m-bold text-main">{{ t(`Reviews.${subject}.view.media.noBadgesTitle`) }}</div>
      <div class="mb-4 max-w-2/4 txt-body-s-regular text-main">
        <div class="mt-2 flex">
          <span class="mr-2"><AwardIcon class="mt-1 lucide-icon-s text-icon-primary" /></span>
          <span v-html="t(`Reviews.${subject}.view.media.noBadgesDescriptionOne`)"></span>
        </div>
        <div class="mt-2 flex">
          <span class="mr-2"><HandHeartIcon class="mt-1 lucide-icon-s text-icon-primary" /></span>
          <span v-html="t(`Reviews.${subject}.view.media.noBadgesDescriptionTwo`)"></span>
        </div>
        <div class="mt-2 flex">
          <span class="mr-2"><StarIcon class="mt-1 lucide-icon-s text-icon-primary" /></span>
          <span v-html="t(`Reviews.${subject}.view.media.noBadgesDescriptionThree`)"></span>
        </div>
      </div>
      <a class="btn-primary-color-m" :href="emptyBadgesLink" rel="noopener noreferrer" target="_blank">
        {{ t(`Reviews.${subject}.view.media.noBadgesButton`) }}
      </a>
    </div>
  </div>
</template>
<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { AwardIcon, HandHeartIcon, StarIcon, CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import badgesFencingImageUrl from '@/components/ReviewsCommon/Fencing/badges_fencing.png'
import FencingBanner from '@/components/ReviewsCommon/Fencing/FencingBanner.vue'
import { baseOmrUrl } from '@/config/env'
import type { AssignedBadgeFieldsFragment } from '@/gql/reviews'
import buildResourceBadges from '@/helpers/buildResourceBadges'

import ResourcesList from './ResourcesList.vue'
import { type Resource } from './types'

const props = defineProps<{
  subject: 'product' | 'agency'
  entityId: string
  slug: string
  title: string
  assignedBadges: AssignedBadgeFieldsFragment[]
  expiredAssignedBadges: AssignedBadgeFieldsFragment[]
  isFenced?: boolean
}>()
const { t, locale } = useI18n()

const entityType = props.subject === 'product' ? 'tool' : 'service'
const helpTooltip = computed(() => t(`Reviews.${props.subject}.view.media.badgesHelp`))
const helpLink = computed(() => t(`Reviews.${props.subject}.view.media.badgesHelpLink`))

const reviewsPDPUrlBadge = computed<string>(() =>
  props.subject === 'product'
    ? baseOmrUrl + reviewsPaths.product({ locale: locale.value, slug: props.slug }, { utm_source: 'badge', utm_medium: 'vendor' })
    : baseOmrUrl + reviewsPaths.agency({ locale: locale.value, slug: props.slug }, { utm_source: 'badge', utm_medium: 'vendor' }),
)

function mapAndSortBadges(badges: AssignedBadgeFieldsFragment[], slug: string) {
  return [...badges]
    .sort((a, b) => (a.badgeType || '').localeCompare(b.badgeType || ''))
    .map((badge) => ({
      title: `${badge.badgeName} ${badge.timeframe}`,
      link: reviewsPaths.badge({ locale: locale.value, slug, badgeId: badge.id }),
      id: badge.id,
    }))
}

const mappedAssignedBadges = computed(() => mapAndSortBadges(props.assignedBadges, props.slug))
const mappedExpiredBadges = computed(() => mapAndSortBadges(props.expiredAssignedBadges, props.slug))
const generatedAssignedBadges: Ref<Resource[]> = computed(() =>
  buildResourceBadges(mappedAssignedBadges.value, baseOmrUrl, props.title, reviewsPDPUrlBadge.value),
)
const generatedExpiredBadges: Ref<Resource[]> = computed(() =>
  buildResourceBadges(mappedExpiredBadges.value, baseOmrUrl, props.title, reviewsPDPUrlBadge.value),
)

const emptyBadgesLink = computed(() => {
  return locale.value === 'en'
    ? 'https://hi.omr.com/en/success-hub/key-facts-about-reviews-the-omr-score-omr-reviews-badges'
    : 'https://hi.omr.com/de/success-hub/bewertungen-auszeichnungen-auf-omr-reviews'
})
</script>
